import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import type { Opportunity, PicklistValue } from '@/api/client';
import { cn } from '@/lib/utils';
import { fmtCurrency, fmtDate } from '@/lib/cobalt-helpers';
import { CompanyMark } from '@/components/CompanyMark';
import { UserAvatar } from '@/components/UserAvatar';
import { StagePicker } from '@/components/StagePicker';
import { StatusPicker } from '@/components/StatusPicker';
import { oppPath } from '@/lib/routes';

interface Props {
  o: Opportunity;
  companyMap: Map<number, string>;
  stageMap: Map<string, string>;
  stageOpts: PicklistValue[];
  statusOpts?: PicklistValue[];
  resourceMap: Map<number, string>;
  myResourceId: number | undefined;
  onStageChange: (id: number, stageId: number) => void;
  onStatusChange?: (id: number, status: number) => void;
  pendingStage?: boolean;
  pendingStatus?: boolean;
}

/**
 * Mobile opportunity card. Lead with the title + company mark, surface the
 * amount big and tabular, stage as a semantic chip.
 */
export function OpportunityCard({ o, companyMap, stageOpts, statusOpts, resourceMap, myResourceId, onStageChange, onStatusChange, pendingStage, pendingStatus }: Props) {
  const company   = o.companyID != null ? companyMap.get(o.companyID) ?? `#${o.companyID}` : null;
  const ownerName = o.ownerResourceID != null ? resourceMap.get(o.ownerResourceID) : null;
  const isMe      = myResourceId != null && o.ownerResourceID === myResourceId;
  const sowValue  = o.userDefinedFields?.find((f) => f.name === 'Statement of Work')?.value?.trim() || null;
  const sowIsUrl  = sowValue ? /^https?:\/\//i.test(sowValue) : false;

  return (
    <Link
      to={oppPath(o.id)}
      className={cn(
        'block rounded-md border border-border bg-card px-3.5 py-3 shadow-sm transition-colors',
        'hover:bg-muted/40 active:bg-muted/60',
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-[11px] tracking-wider text-muted-foreground">#{o.id}</span>
        {o.projectedCloseDate && (
          <span className="shrink-0 text-[11px] text-muted-foreground">Close · {fmtDate(o.projectedCloseDate)}</span>
        )}
      </div>
      <div className="mt-1 line-clamp-2 text-[14.5px] font-[550] leading-snug tracking-[-0.005em] text-foreground">
        {o.title}
      </div>
      {company && (
        <div className="mt-2 flex items-center gap-2">
          <CompanyMark name={company} size={18}/>
          <span className="truncate text-xs text-foreground/80">{company}</span>
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <StagePicker
          stageId={o.stage}
          stageOpts={stageOpts}
          onSelect={(stageId) => onStageChange(o.id, stageId)}
          pending={pendingStage}
          size="sm"
        />
        {statusOpts && onStatusChange && (
          <StatusPicker
            statusId={o.status ?? null}
            statusOpts={statusOpts}
            onSelect={(status) => onStatusChange(o.id, status)}
            pending={pendingStatus}
            size="sm"
          />
        )}
        {o.amount != null && (
          <span className="text-[13px] font-semibold tabular-nums text-foreground">{fmtCurrency(o.amount)}</span>
        )}
        {o.probability != null && (
          <div className="flex items-center gap-1.5">
            <div className="h-1.5 w-12 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-brand" style={{ width: `${o.probability}%` }}/>
            </div>
            <span className="text-[11.5px] tabular-nums text-muted-foreground">{o.probability}%</span>
          </div>
        )}
        <span className="ml-auto flex items-center gap-2">
          {sowValue && (
            sowIsUrl ? (
              // Not an <a>: the whole card is already a link, and nested anchors are invalid HTML.
              <span
                role="link"
                tabIndex={0}
                title="Open Statement of Work"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(sowValue, '_blank', 'noreferrer'); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); window.open(sowValue, '_blank', 'noreferrer'); } }}
                className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <FileText className="h-3.5 w-3.5" />
              </span>
            ) : (
              <span title={sowValue} className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground">
                <FileText className="h-3.5 w-3.5" />
              </span>
            )
          )}
          {ownerName && (
            <span className="flex items-center gap-1.5">
              <UserAvatar name={ownerName} size={18}/>
              <span className={cn('text-[11.5px]', isMe ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                {isMe ? 'You' : ownerName.split(' ')[0]}
              </span>
            </span>
          )}
        </span>
      </div>
    </Link>
  );
}
